import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { TeamMemberStatus } from 'webapp/enums/enums';

import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { Team, User } from 'webapp/models-custom';

export interface Args {
	model: Team;
	hidePendingBadge?: boolean;
	isPreview?: boolean;
}

export default class TeamsMembersList extends Component<Args> {
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/teams') declare mmTeamsService: Service.ModelManager.Teams;

	maxVisibleMembers = 5;

	get model() {
		return this.args.model;
	}

	@cached
	get members() {
		const members: User[] = [],
			teamMembers = this.model.teamMembers.filter(
				(member) => member.status === TeamMemberStatus.MEMBER,
			);

		teamMembers.forEach((member) => {
			this.mmUsersService.activatedUserModels.forEach((user) => {
				if (user.numericId === member.id) {
					members.push(user);
				}
			});
		});

		return members;
	}

	get visibleMembers() {
		return this.members.slice(0, this.maxVisibleMembers);
	}

	get hasHiddenMembers() {
		return this.members.length > this.maxVisibleMembers;
	}

	get hiddenMembersCount() {
		return this.members.length - this.maxVisibleMembers;
	}

	get membersText() {
		const count = this.members.length;
		return `${count} member${count > 1 ? 's' : ''}`;
	}

	get pendingMembersCount() {
		return (
			this.model.teamMembers.filter((member) => member.status === TeamMemberStatus.REQUEST)
				.length ?? 0
		);
	}

	get isPendingBadgeShown() {
		return this.pendingMembersCount > 0 && !this.args.hidePendingBadge;
	}

	@action
	openMembersListDialog() {
		const team = this.model;

		if (team && !this.args.isPreview) {
			this.mmTeamsService.openManageTeamMembersPopup(team);
		}
	}
	<template>
		<div
			role="button"
			class="teams-members-list {{if @isPreview 'teams-members-list--preview'}}"
			{{on "click" this.openMembersListDialog}}
		>
			<div class="teams-members-list__container">
				{{#each this.visibleMembers as |member|}}
					<div class="teams-members-list__item">
						<AvatarOrInitialsComponent
							@name={{member.name}}
							@avatar={{member.avatar}}
						/>
						<ToolTipComponent @text={{member.name}} @position="bottom" />
					</div>
				{{/each}}
				{{#if this.hasHiddenMembers}}
					<div class="teams-members-list__item teams-members-list__item--count">
						+{{this.hiddenMembersCount}}
					</div>
				{{/if}}
			</div>
			<span class="teams-members-list__count">{{this.membersText}}</span>
			{{#if this.isPendingBadgeShown}}
				<span class="teams-members-list__pending-badge">
					{{this.pendingMembersCount}}
					pending
				</span>
			{{/if}}
		</div>
	</template>
}
